import type { Employee } from "@/types/tycoon";
import { EMPLOYEE_CONSTANTS, TIME_CONSTANTS } from "@/config/gameConstants";
import { EMPLOYEE_FORMULAS } from "@/config/gameFormulas";
import { generateId } from "@/db/connection";

export interface EmployeeRole {
  id: string;
  name: string;
  baseSalary: number;
  skills: {
    [key: string]: number;
  };
  disabled?: boolean;
  disabledReason?: string;
}

export interface HiringProcess {
  role: string;
  count: number;
  startDate: {
    day: number;
    month: number;
    year: number;
  };
  daysRemaining: number;
}

// Dynamic employee stats type based on EMPLOYEE_ROLES
export type EmployeeStats = {
  [K in keyof typeof EMPLOYEE_ROLES]: number;
} & {
  total: number;
};

// Helper to get ordered list of employee roles for UI display
export const EMPLOYEE_ROLE_ORDER = [
  "engineer",
  "designer",
  "marketer",
  "manager",
  "researcher",
  "tester",
  "assemblyWorker",
] as const;

// Helper to get display name for a role
export function getRoleDisplayName(roleId: string): string {
  return EMPLOYEE_ROLES[roleId]?.name || roleId;
}

// Define employee roles and their base properties
export const EMPLOYEE_ROLES: Record<string, EmployeeRole> = {
  engineer: {
    id: "engineer",
    name: "Engineer",
    baseSalary: EMPLOYEE_FORMULAS.getAnnualToDailySalary(
      EMPLOYEE_CONSTANTS.annualSalaries.engineer,
    ),
    skills: EMPLOYEE_CONSTANTS.skills.engineer,
  },
  designer: {
    id: "designer",
    name: "Designer",
    baseSalary: EMPLOYEE_FORMULAS.getAnnualToDailySalary(
      EMPLOYEE_CONSTANTS.annualSalaries.designer,
    ),
    skills: EMPLOYEE_CONSTANTS.skills.designer,
  },
  marketer: {
    id: "marketer",
    name: "Marketer",
    baseSalary: EMPLOYEE_FORMULAS.getAnnualToDailySalary(
      EMPLOYEE_CONSTANTS.annualSalaries.marketer,
    ),
    skills: EMPLOYEE_CONSTANTS.skills.marketer,
  },
  manager: {
    id: "manager",
    name: "Manager",
    baseSalary: EMPLOYEE_FORMULAS.getAnnualToDailySalary(
      EMPLOYEE_CONSTANTS.annualSalaries.manager,
    ),
    skills: EMPLOYEE_CONSTANTS.skills.manager,
  },
  researcher: {
    id: "researcher",
    name: "Researcher",
    baseSalary: EMPLOYEE_FORMULAS.getAnnualToDailySalary(
      EMPLOYEE_CONSTANTS.annualSalaries.researcher,
    ),
    skills: EMPLOYEE_CONSTANTS.skills.researcher,
  },
  tester: {
    id: "tester",
    name: "Tester",
    baseSalary: EMPLOYEE_FORMULAS.getAnnualToDailySalary(
      EMPLOYEE_CONSTANTS.annualSalaries.tester,
    ),
    skills: EMPLOYEE_CONSTANTS.skills.tester,
  },
  assemblyWorker: {
    id: "assemblyWorker",
    name: "Assembly Worker",
    baseSalary: EMPLOYEE_FORMULAS.getAnnualToDailySalary(
      EMPLOYEE_CONSTANTS.annualSalaries.assemblyWorker,
    ),
    skills: EMPLOYEE_CONSTANTS.skills.assemblyWorker,
  },
};

export class EmployeeService {
  private static instance: EmployeeService;

  private constructor() {}

  static getInstance(): EmployeeService {
    if (!EmployeeService.instance) {
      EmployeeService.instance = new EmployeeService();
    }
    return EmployeeService.instance;
  }

  // Create a new employee
  createEmployee(role: EmployeeRole, customName?: string): Employee {
    const names = this.generateEmployeeName();

    return {
      id: generateId("emp_"),
      name: customName || names,
      role: role.id,
      salary: role.baseSalary,
      skills: { ...role.skills },
      experience: 0,
      performance: EMPLOYEE_CONSTANTS.initialPerformance,
      happiness: EMPLOYEE_CONSTANTS.initialHappiness,
    };
  }

  // Generate random employee name
  private generateEmployeeName(): string {
    const firstNames = [
      "Alex",
      "Jordan",
      "Taylor",
      "Casey",
      "Morgan",
      "Riley",
      "Cameron",
      "Avery",
      "Quinn",
      "Skyler",
      "Drew",
      "Blake",
      "Jamie",
      "Leslie",
      "Robin",
      "Pat",
    ];
    const lastNames = [
      "Smith",
      "Johnson",
      "Williams",
      "Brown",
      "Jones",
      "Garcia",
      "Miller",
      "Davis",
      "Rodriguez",
      "Martinez",
      "Hernandez",
      "Lopez",
      "Gonzalez",
      "Wilson",
      "Anderson",
      "Thomas",
    ];

    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];

    return `${firstName} ${lastName}`;
  }

  // Calculate total daily salary costs
  calculateDailySalaryCosts(employees: Employee[]): number {
    return EMPLOYEE_FORMULAS.getDailySalaryCosts(employees);
  }

  // Get employee statistics by role
  getEmployeeStats(employees: Employee[]): EmployeeStats {
    const stats: Record<string, number> = {
      total: 0,
    };

    // Initialize all roles to 0
    Object.keys(EMPLOYEE_ROLES).forEach((role) => {
      stats[role] = 0;
    });

    employees.forEach((employee) => {
      stats.total++;
      // Role is already stored as the role ID (e.g., 'engineer', 'assemblyWorker')
      if (employee.role in EMPLOYEE_ROLES) {
        stats[employee.role]++;
      }
    });

    return stats as EmployeeStats;
  }

  // Calculate severance cost for firing employees
  calculateSeveranceCost(employees: Employee[]): number {
    return employees.reduce((total, employee) => {
      const monthlySalary = employee.salary * TIME_CONSTANTS.daysPerMonth;
      return total + EMPLOYEE_FORMULAS.getSeveranceCost(monthlySalary);
    }, 0);
  }

  // Calculate reputation impact for firing employees
  calculateReputationImpact(employeeCount: number): number {
    return EMPLOYEE_FORMULAS.getReputationImpact(employeeCount);
  }

  // Update hiring processes (advance by one day)
  updateHiringProcesses(processes: HiringProcess[]): {
    updatedProcesses: HiringProcess[];
    completedHires: Array<{ role: string; count: number }>;
  } {
    const updatedProcesses: HiringProcess[] = [];
    const completedHires: Array<{ role: string; count: number }> = [];

    processes.forEach((process) => {
      const updatedProcess = { ...process };
      updatedProcess.daysRemaining--;

      if (updatedProcess.daysRemaining <= 0) {
        completedHires.push({
          role: process.role,
          count: process.count,
        });
      } else {
        updatedProcesses.push(updatedProcess);
      }
    });

    return { updatedProcesses, completedHires };
  }

  // Create employees from completed hiring processes
  createEmployeesFromHiring(
    completedHires: Array<{ role: string; count: number }>,
  ): Employee[] {
    const newEmployees: Employee[] = [];

    completedHires.forEach((hire) => {
      // hire.role should already be the role ID
      const role = EMPLOYEE_ROLES[hire.role];

      if (role) {
        for (let i = 0; i < hire.count; i++) {
          newEmployees.push(this.createEmployee(role));
        }
      }
    });

    return newEmployees;
  }

  // Fire employees by role
  fireEmployeesByRole(
    employees: Employee[],
    role: string,
    count: number,
  ): {
    remainingEmployees: Employee[];
    firedEmployees: Employee[];
  } {
    const firedEmployees: Employee[] = [];
    const remainingEmployees: Employee[] = [];

    let firedCount = 0;

    employees.forEach((employee) => {
      if (employee.role === role && firedCount < count) {
        firedEmployees.push(employee);
        firedCount++;
      } else {
        remainingEmployees.push(employee);
      }
    });

    return { remainingEmployees, firedEmployees };
  }

  // Update employee performance and happiness (called daily)
  updateEmployeeMetrics(employees: Employee[]): Employee[] {
    return employees.map((employee) => {
      // Small daily fluctuations
      const performanceChange = EMPLOYEE_FORMULAS.getDailyPerformanceChange();
      const happinessChange = EMPLOYEE_FORMULAS.getDailyHappinessChange();

      return {
        ...employee,
        performance: Math.max(
          EMPLOYEE_CONSTANTS.minPerformance,
          Math.min(
            EMPLOYEE_CONSTANTS.maxPerformance,
            employee.performance + performanceChange,
          ),
        ),
        happiness: Math.max(
          0,
          Math.min(100, employee.happiness + happinessChange),
        ),
        experience: Math.min(
          100,
          employee.experience + EMPLOYEE_CONSTANTS.dailyExperienceGain,
        ),
      };
    });
  }

  // Calculate team productivity multiplier based on employee composition
  calculateProductivityMultiplier(employees: Employee[]): number {
    const stats = this.getEmployeeStats(employees);
    return EMPLOYEE_FORMULAS.getProductivityMultiplier(stats, employees);
  }
}

export const employeeService = EmployeeService.getInstance();
